import { ReactNode } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";

import { Picture } from "@/components/wpl/image/src/image";
import { wacToken } from "@/libraries/design/foundations";
import { useLocalizedHref } from "@/libraries/web-framework/src/localization/use-localized-href";
import { Row } from "@/libraries/wpl/foundations/src/grid";
import { experiments } from "@/utils/experiment/manifest";
import { useExperiment } from "@/utils/experiment/use-experiment";

const GartnerStyles = css({
    alignItems: "center",
    display: "flex",
    lineHeight: "1.25",
    justifyContent: "center",
    padding: "100px 0 50px",
    marginBlockStart: "-30px",
    backgroundImage: `linear-gradient(180deg, ${wacToken(
        "color.extended.blue100"
    )} 0%, ${wacToken("color.neutral.light0")} 100%)`,
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    a: {
        color: wacToken("color.primary.blue")
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    h2: {
        fontSize: "2.5rem",
        marginBlockStart: 0,
        [media.below.md]: {
            fontSize: "1.8rem"
        }
    }
});

export const GartnerSection = ({
    experimentEnabled
}: {
    experimentEnabled?: boolean;
}) => {
    const getGwp4948GartnerJiraProductTourExperiment = useExperiment(
        experiments.gwp4948GartnerJiraProductTourExperiment
    );

    const gwp4948GartnerJiraProductTourExperimentIsActive =
        experimentEnabled ||
        (getGwp4948GartnerJiraProductTourExperiment !== undefined &&
            getGwp4948GartnerJiraProductTourExperiment.type === "enrolled" &&
            getGwp4948GartnerJiraProductTourExperiment.value === "test");

    const intl = useIntl();

    const gartnerImage =
        // eslint-disable-next-line local/no-non-orangelogic-images-eslint
        "https://wac-cdn.atlassian.com/misc-assets/webp-images/gartner-jsw-left-img.webp";

    const gartnerMarketingHref = useLocalizedHref(
        "https://www.atlassian.com/gartner/magic-quadrant-marketing-work-management"
    );

    const gartnerDevopsHref = useLocalizedHref(
        "https://www.atlassian.com/gartner/magic-quadrant-devops"
    );

    const gartnerMarketingLink = (children: ReactNode) => (
        <a href={gartnerMarketingHref}>{children}</a>
    );

    const gartnerDevopsLink = (children: ReactNode) => (
        <a href={gartnerDevopsHref}>{children}</a>
    );

    if (gwp4948GartnerJiraProductTourExperimentIsActive) {
        return (
            <section css={GartnerStyles}>
                <Row alignItems="center">
                    <Picture image={{ src: gartnerImage, alt: "" }} />

                    <div>
                        <h2>
                            {intl.formatMessage({
                                id: "JiraSoftwarePageTemplate.GartnerSection.header",
                                defaultMessage:
                                    "Atlassian named a Leader in the 2024 Gartner® Magic Quadrant"
                            })}
                        </h2>
                        <FormattedMessage
                            id="JiraSoftwarePageTemplate.GartnerSection.body"
                            defaultMessage={`The best work management solution for all teams — Atlassian is the only platform to be recognized as a Leader for both <gartnerMarketingLink>Marketing</gartnerMarketingLink> and <gartnerDevopsLink>DevOps</gartnerDevopsLink>`}
                            values={{
                                gartnerMarketingLink,
                                gartnerDevopsLink
                            }}
                        />
                    </div>
                </Row>
            </section>
        );
    }
    return <></>;
};
